import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from '../../settingsUtil';

/**
 * Keep this as a plain object so product and support teams can adjust the beta
 * access shape without digging through mutation plumbing.
 */
export const betaPortalPermissions = {
  authorizations: 2,
  developer: {
    api_logs: 2,
    base: 2,
    native_apps: 2,
    secret_key: 2,
    web_hooks: 2,
  },
  disputes: 2,
  export_payments: 1,
  invoices: {
    base: 2,
    resend: 1,
  },
  manual_payments: 2,
  onboarding: 2,
  payment_links: 2,
  recurring: {
    base: 2,
    payment_method: 2,
  },
  settings: {
    base: 2,
    fee_matrix: 1,
    funding_bank: 2,
  },
  settlements: 2,
  transactions: {
    base: 2,
    resend: 1,
  },
  users: {
    base: 2,
    permissions: 2,
  },
} as const;

/**
 * Match the shared portal-head announcement language while keeping copy local
 * to this feature so future migration wording changes stay centralized.
 */
export const betaPortalBannerMessage = 'Try the new portal (beta).';

export const BETA_PORTAL_LEARN_MORE_URL =
  'https://www.paytheory.com/blog/a-new-merchant-portal-experience';

export const betaPortalLearnMoreCta = 'Learn more';

export const betaPortalLoginCta = 'Log in to new portal';

export const betaPortalDismissCta = 'Not now';

export const betaPortalCreateErrorMessage =
  "We couldn't set up your new portal access. Please try again or contact support if the issue continues.";

export const betaPortalExistingAccountHeader = 'New portal access is ready';

export const getEmailSentMessage = (email: string): string =>
  `Login email has been sent to ${email}`;

export const betaPortalExistingAccountMessage =
  'You already have access to the new portal. Please log in here:';

export const getNewPortalUrl = (
  partner = process.env.REACT_APP_PARTNER,
  stage = process.env.REACT_APP_STAGE,
): string => `https://${partner}.dashboard.${stage}.com`;

/**
 * Returns true when the error string from the backend indicates the Autheory
 * user already exists, so we can show a friendly sign-in prompt instead of an
 * error.
 */
export const isUserAlreadyExistsError = (errorMessage: string): boolean => {
  const lower = errorMessage.toLowerCase();
  return lower.includes('already exists') || lower.includes('email_in_use');
};

/**
 * The backend contract requires first name, last name, and email. If those are
 * missing in the current session, stop before firing the mutation.
 */
export const betaPortalMissingProfileMessage =
  "We couldn't create a new portal user because your profile is missing a first name, last name, or email. Please contact support.";

/**
 * Browser-persisted record that suppresses the prompt for one
 * email-and-merchant combination.
 */
export type MerchantPortalMigrationDismissal = {
  dismissedAt: string;
  email: string;
  merchant_uid: string;
};

/**
 * Runtime inputs used to decide whether the migration prompt should render.
 */
export type MerchantPortalMigrationEligibilityInput = {
  email?: string;
  merchantUID?: string;
};

const PORTAL_MIGRATION_STORAGE_PREFIX =
  'PT_MERCHANT_PORTAL_MIGRATION_DISMISSAL';
const DISMISSAL_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Normalize env inputs before comparing them because partner names arrive from
 * deployment config while dismissal keys are user-derived.
 */
export const normalizePortalMigrationValue = (
  value?: string | null,
): string => {
  return value?.trim().toLowerCase() ?? '';
};

/**
 * Key dismissal state by user+merchant so one dismissed merchant does not hide
 * the prompt for every merchant that shares the same email address.
 */
export const getMerchantPortalMigrationDismissalKey = (
  email?: string,
  merchantUID?: string,
): string | null => {
  const normalizedEmail = normalizePortalMigrationValue(email);
  const normalizedMerchantUID = normalizePortalMigrationValue(merchantUID);

  if (!normalizedEmail || !normalizedMerchantUID) {
    return null;
  }

  return `${PORTAL_MIGRATION_STORAGE_PREFIX}:${normalizedEmail}:${normalizedMerchantUID}`;
};

/**
 * Build the stored dismissal payload in one place so successful provisioning
 * and existing-user detection both write the same schema for future expiration
 * logic.
 */
export const buildMerchantPortalMigrationDismissal = (
  email?: string,
  merchantUID?: string,
  dismissedAt = new Date().toISOString(),
): MerchantPortalMigrationDismissal | null => {
  if (!email || !merchantUID) {
    return null;
  }

  return {
    dismissedAt,
    email,
    merchant_uid: merchantUID,
  };
};

/**
 * Parse the stored dismissal record defensively so a malformed local-storage
 * value never breaks app render.
 */
export const getMerchantPortalMigrationDismissal = (
  email?: string,
  merchantUID?: string,
): MerchantPortalMigrationDismissal | null => {
  const key = getMerchantPortalMigrationDismissalKey(email, merchantUID);
  if (!key) {
    return null;
  }

  const value = getLocalStorage(key)();
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as MerchantPortalMigrationDismissal;
    if (
      parsed?.email &&
      parsed?.merchant_uid &&
      parsed?.dismissedAt &&
      normalizePortalMigrationValue(parsed.email) ===
        normalizePortalMigrationValue(email) &&
      normalizePortalMigrationValue(parsed.merchant_uid) ===
        normalizePortalMigrationValue(merchantUID)
    ) {
      return parsed;
    }
  } catch (error) {
    console.log(error);
  }

  return null;
};

/**
 * Store the raw email and merchant UID alongside the timestamp so future
 * mandatory-mode work can inspect or expire dismissals without changing format.
 */
export const setMerchantPortalMigrationDismissal = (
  dismissal: MerchantPortalMigrationDismissal,
): void => {
  const key = getMerchantPortalMigrationDismissalKey(
    dismissal.email,
    dismissal.merchant_uid,
  );
  if (!key) {
    return;
  }

  setLocalStorage(key)(JSON.stringify(dismissal));
};

/**
 * Remove a stored dismissal so QA or future forced-rollout logic can re-show
 * the prompt for the current browser.
 */
export const removeMerchantPortalMigrationDismissal = (
  email?: string,
  merchantUID?: string,
): void => {
  const key = getMerchantPortalMigrationDismissalKey(email, merchantUID);
  if (!key) {
    return;
  }

  removeLocalStorage(key)();
};

/**
 * Removes every dismissal record for all email+merchant combinations stored in
 * this browser. Intended for local development: call
 * `window.__resetPortalMigrationPrompt()` in the browser console to force the
 * banner to reappear without waiting for the 24-hour TTL to expire.
 */
export const resetAllMerchantPortalMigrationDismissals = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(PORTAL_MIGRATION_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    // localStorage unavailable — nothing to reset
  }
};

/**
 * The banner only renders when the current user is known and that user has
 * not already dismissed the prompt for this merchant in this browser.
 */
export const shouldShowMerchantPortalMigrationPrompt = ({
  email,
  merchantUID,
}: MerchantPortalMigrationEligibilityInput): boolean => {
  if (!email || !merchantUID) {
    return false;
  }

  const dismissal = getMerchantPortalMigrationDismissal(email, merchantUID);
  if (!dismissal) {
    return true;
  }

  const dismissedAt = new Date(dismissal.dismissedAt).getTime();
  return Date.now() - dismissedAt >= DISMISSAL_TTL_MS;
};
